import { useEffect, useRef, useState } from "react";
import { Loader2, Save, Truck, MapPin, CalendarDays, Camera } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { useAuth } from "../context/AuthContext";
import { FotoImg } from "../components/FotoBuktiDialog";
import { ScanSlipButton } from "../components/ScanSlipButton";
import api, { formatApiError } from "../api";

const statusBadge = {
  Berangkat: "bg-blue-100 text-blue-800 border border-blue-200",
  Bongkar: "bg-amber-100 text-amber-800 border border-amber-200",
};

const FotoSection = ({ trip, refreshKey }) => {
  const [fotos, setFotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = () => {
    api.get(`/supir-trips/${trip.id}/foto`).then(({ data }) => setFotos(data)).catch(() => {});
  };
  useEffect(() => { load(); }, [trip.id, refreshKey]);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { toast.error("Ukuran foto maksimal 8 MB"); return; }
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      await api.post(`/supir-trips/${trip.id}/foto`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Foto bukti timbang terunggah");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="border-t border-zinc-100 pt-3 space-y-2">
      <div className="flex items-center justify-between">
        <p className="text-xs font-medium text-zinc-600 flex items-center gap-1.5">
          <Camera className="w-3.5 h-3.5" /> Foto Bukti Timbang {fotos.length > 0 && <span className="text-zinc-400">({fotos.length})</span>}
        </p>
        <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" capture="environment"
          className="hidden" onChange={upload} data-testid={`foto-input-${trip.ID_Trip}`} />
        <Button data-testid={`upload-foto-${trip.ID_Trip}`} type="button" variant="outline" size="sm"
          disabled={uploading} onClick={() => fileRef.current?.click()}
          className="btn-touch border-emerald-200 text-emerald-700 hover:bg-emerald-50">
          {uploading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Camera className="w-4 h-4 mr-1.5" />} Tambah Foto
        </Button>
      </div>
      {fotos.length > 0 && (
        <div className="flex gap-2 overflow-x-auto pb-1">
          {fotos.map((f) => (
            <FotoImg key={f.id} fotoId={f.id} testId={`foto-thumb-supir-${f.id}`}
              className="w-16 h-16 object-cover rounded-md border border-zinc-200 flex-shrink-0" />
          ))}
        </div>
      )}
    </div>
  );
};

const TripTonaseCard = ({ trip, onSaved }) => {
  const [muat, setMuat] = useState(trip.Tonase_Muat_KG ?? "");
  const [bongkar, setBongkar] = useState(trip.Tonase_Bongkar_KG ?? "");
  const [saving, setSaving] = useState(false);
  const [scanKey, setScanKey] = useState(0);
  const canBongkar = trip.Status_Trip === "Bongkar";
  const susut = muat !== "" && bongkar !== "" && canBongkar
    ? (parseFloat(bongkar || 0) - parseFloat(muat || 0)).toFixed(2) : null;

  const applyScan = (data) => {
    const warns = [];
    const platTrip = (trip.Plat_Truk || "").replace(/[^A-Za-z0-9]/g, "").toUpperCase();
    if (data.plat_normalized && platTrip && data.plat_normalized !== platTrip)
      warns.push(`No. kendaraan slip ${data.vec_no} tidak cocok dengan truk trip (${trip.Plat_Truk})`);
    if (data.komoditas && data.komoditas !== trip.Jenis_Komoditas)
      warns.push(`Produk slip ${data.product_code} (${data.komoditas}) tidak cocok dengan komoditas trip (${trip.Jenis_Komoditas})`);
    if (data.net_weight_kg != null) {
      if (canBongkar) setBongkar(String(data.net_weight_kg));
      else setMuat(String(data.net_weight_kg));
      toast.success(`Slip terbaca — ${Number(data.net_weight_kg).toLocaleString("id-ID")} KG diisi ke Tonase ${canBongkar ? "Bongkar" : "Muat"}`, { duration: 6000 });
    } else {
      toast.error("Berat (NET WEIGHT) tidak terbaca dari slip — isi manual");
    }
    warns.forEach((w) => toast.warning(w, { duration: 9000 }));
    if (data.foto_saved) {
      setScanKey((k) => k + 1);
      toast.info("Foto slip diarsipkan ke Foto Bukti Timbang", { duration: 5000 });
    }
  };

  const save = async (e) => {
    e.preventDefault();
    const payload = {};
    if (muat !== "") payload.Tonase_Muat_KG = parseFloat(muat);
    if (canBongkar && bongkar !== "") payload.Tonase_Bongkar_KG = parseFloat(bongkar);
    if (!Object.keys(payload).length) { toast.error("Isi tonase terlebih dahulu"); return; }
    setSaving(true);
    try {
      const { data } = await api.put(`/supir-trips/${trip.id}/tonase`, payload);
      if (data.Status_Trip === "Selesai") {
        toast.success(`Tonase ${trip.ID_Trip} tersimpan — trip SELESAI`, { duration: 6000 });
      } else if (data.Status_Trip !== trip.Status_Trip) {
        toast.success(`Tonase ${trip.ID_Trip} tersimpan — status menjadi ${data.Status_Trip}`, { duration: 5000 });
      } else {
        toast.success(`Tonase ${trip.ID_Trip} tersimpan`);
      }
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card data-testid={`supir-trip-card-${trip.ID_Trip}`} className="border-zinc-200 shadow-sm">
      <CardHeader className="pb-3 flex flex-row items-center justify-between space-y-0">
        <span className="font-semibold text-zinc-900">{trip.ID_Trip}</span>
        <Badge className={`${statusBadge[trip.Status_Trip] || ""} font-medium`}>{trip.Status_Trip}</Badge>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="text-sm text-zinc-600 space-y-1">
          <p className="flex items-center gap-2"><CalendarDays className="w-4 h-4 text-zinc-400" />{trip.Tanggal_Muat} &bull; DO: {trip.No_DO}</p>
          <p className="flex items-center gap-2"><Truck className="w-4 h-4 text-zinc-400" />{trip.Plat_Truk} &bull; {trip.Jenis_Komoditas}</p>
          <p className="flex items-center gap-2"><MapPin className="w-4 h-4 text-zinc-400" />{trip.PKS_Asal} → {trip.Tujuan_Bongkar}</p>
        </div>
        <ScanSlipButton testId={`scan-slip-${trip.ID_Trip}`} tripId={trip.id} className="w-full btn-touch" onResult={applyScan} />
        <form onSubmit={save} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Tonase Muat (KG)</Label>
              <Input data-testid={`tonase-muat-input-${trip.ID_Trip}`} type="number" step="0.01" min="0"
                inputMode="decimal" value={muat} onChange={(e) => setMuat(e.target.value)}
                placeholder="0" className="bg-white num-hc input-touch" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Tonase Bongkar (KG)</Label>
              <Input data-testid={`tonase-bongkar-input-${trip.ID_Trip}`} type="number" step="0.01" min="0"
                inputMode="decimal" value={bongkar} onChange={(e) => setBongkar(e.target.value)}
                placeholder={canBongkar ? "0" : "Terkunci"} disabled={!canBongkar}
                className="bg-white num-hc input-touch disabled:bg-zinc-100" />
            </div>
          </div>
          {!canBongkar && (
            <p className="text-xs text-zinc-500">Simpan tonase muat untuk mengubah status menjadi "Bongkar", lalu isi tonase bongkar untuk menyelesaikan trip.</p>
          )}
          {canBongkar && (
            <p className="text-xs text-zinc-500">Menyimpan tonase bongkar akan menyelesaikan trip ini secara otomatis.</p>
          )}
          {susut !== null && !isNaN(susut) && (
            <p className="text-xs text-zinc-600">Penyusutan: <span className="num-hc font-semibold">{susut} KG</span></p>
          )}
          <Button data-testid={`save-tonase-${trip.ID_Trip}`} type="submit" disabled={saving}
            className="w-full btn-touch bg-emerald-700 hover:bg-emerald-800 text-white transition-colors duration-200">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Simpan Tonase
          </Button>
        </form>
        <FotoSection trip={trip} refreshKey={scanKey} />
      </CardContent>
    </Card>
  );
};

export default function SupirTonasePage() {
  const { user } = useAuth();
  const [trips, setTrips] = useState(null);
  const [error, setError] = useState("");

  const load = () => {
    api.get("/supir-trips")
      .then(({ data }) => { setTrips(data); setError(""); })
      .catch((err) => { setTrips([]); setError(formatApiError(err.response?.data?.detail)); });
  };
  useEffect(() => { load(); }, []);

  return (
    <div data-testid="supir-tonase-page" className="space-y-6 max-w-3xl">
      <div>
        <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-zinc-900">Input Tonase</h1>
        <p className="text-sm text-zinc-500 mt-1">
          Trip aktif Anda{user?.Nama_Supir ? ` (${user.Nama_Supir})` : ""} — isi tonase muat &amp; bongkar harian
        </p>
      </div>
      {trips === null ? (
        <div className="flex items-center justify-center py-16 text-zinc-400"><Loader2 className="w-6 h-6 animate-spin" /></div>
      ) : error ? (
        <div data-testid="supir-error" className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg p-4">{error}</div>
      ) : trips.length === 0 ? (
        <div data-testid="supir-empty-state" className="bg-white border border-zinc-200 rounded-lg p-10 text-center text-zinc-500 text-sm">
          Tidak ada trip aktif untuk Anda saat ini. Trip akan muncul di sini setelah berstatus "Berangkat".
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {trips.map((t) => <TripTonaseCard key={t.id} trip={t} onSaved={load} />)}
        </div>
      )}
    </div>
  );
}
